import { useState } from "react";
import {
  CalendarDays,
  TrendingUp,
  TrendingDown,
  Minus,
  BarChart3,
  Banknote,
  Clock,
  MapPin,
  Settings,
  GraduationCap,
  Calendar as CalendarIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Button, Skeleton } from "@sportscanner/ui";
import { apiGet } from "@/lib/api";
import { formatCourtLabel } from "@/components/booking/helpers";
import { useMe, useSubscriptionActive, hasPermission } from "@/lib/me";
import ClubScopeShell from "@/components/club-scope-shell";
import ShareClubButton from "@/components/share-club-button";

interface TrendValue {
  value: number;
  change: number;
  trend: "up" | "down" | "flat";
}

interface DashboardData {
  today: TrendValue;
  week: TrendValue;
  occupancy: TrendValue;
  // revenue + weeklyRevenue are only present when the caller has view_revenue.
  revenue?: TrendValue;
  upcomingBookings: Array<{
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    playerName: string;
    courtId: string;
    courtName: string;
    sportId: string;
    sportName: string;
  }>;
  courtStatus: Array<{
    courtId: string;
    name: string;
    sportName: string;
    isIndoor: boolean;
    status: "free" | "occupied";
    until: string | null;
    nextStartTime: string | null;
  }>;
  weeklyRevenue?: Array<{
    dayKey: string;
    date: string;
    current: number;
    previous: number;
  }>;
  popularTimes: Array<{ hour: string; bookings: number }>;
}

interface CoachStats {
  coaches: Array<{
    id: string;
    name: string;
    upcoming: number;
    next7: number;
  }>;
  totalUpcoming: number;
  next7Sessions: number;
}

function formatPriceShort(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1000) return `${Math.round(value / 1000)}K`;
  return value.toString();
}

function formatRSD(value: number): string {
  return new Intl.NumberFormat("sr-Latn-RS").format(value);
}

function StatCard({
  label,
  value,
  unit,
  icon,
  iconColor,
  iconBg,
  trendData,
  trendLabel,
  trendFormatter,
}: {
  label: string;
  value: string | number;
  unit?: string;
  icon: React.ReactNode;
  iconColor: string;
  iconBg: string;
  trendData: TrendValue;
  trendLabel: string;
  trendFormatter: (change: number) => string;
}) {
  const TrendIcon =
    trendData.trend === "up"
      ? TrendingUp
      : trendData.trend === "down"
        ? TrendingDown
        : Minus;
  const trendColor =
    trendData.trend === "up"
      ? "text-emerald-600"
      : trendData.trend === "down"
        ? "text-red-500"
        : "text-neutral-400";
  const sign = trendData.change > 0 ? "+" : "";
  return (
    <div className="bg-white rounded-xl p-4 sm:p-5 border border-neutral-200">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-neutral-500 mb-1">{label}</p>
          <p className="text-2xl sm:text-3xl font-bold text-neutral-800 leading-none">
            {value}
          </p>
          {unit && <p className="text-sm text-neutral-500 mt-1">{unit}</p>}
        </div>
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconBg}`}
        >
          <span className={iconColor}>{icon}</span>
        </div>
      </div>
      <div
        className={`flex items-center gap-1 mt-3 text-sm font-medium ${trendColor}`}
      >
        <TrendIcon className="w-4 h-4" />
        <span>
          {sign}
          {trendFormatter(trendData.change)} {trendLabel}
        </span>
      </div>
    </div>
  );
}

function StatsSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="bg-white rounded-xl p-5 border border-neutral-200 space-y-3"
        >
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-3 w-32" />
        </div>
      ))}
    </div>
  );
}

function DashboardContent({ clubId }: { clubId: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, club } = useMe();
  const isStaff = user?.role === "STAFF";
  // Revenue is hidden when the user lacks permission OR the club is free (no
  // charging means no revenue to show).
  const showRevenue =
    hasPermission(user, "view_revenue") && !(club?.isFree ?? false);
  const subscriptionActive = useSubscriptionActive();

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "dashboard", clubId],
    queryFn: () =>
      apiGet<DashboardData>(`/api/admin/clubs/${clubId}/dashboard`),
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <StatsSkeleton />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Skeleton className="h-64 rounded-xl" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center">
        <p className="text-sm text-neutral-500">
          {t("errors.api.INTERNAL_ERROR")}
        </p>
      </div>
    );
  }

  const locale =
    i18n.language === "ru"
      ? "ru"
      : i18n.language === "en"
        ? "en"
        : "sr-Latn-RS";
  const formatNumber = (n: number) => new Intl.NumberFormat(locale).format(n);

  const weeklyRevenue = data.weeklyRevenue ?? [];
  const maxRevenueDay =
    weeklyRevenue.length > 0
      ? Math.max(...weeklyRevenue.flatMap((d) => [d.current, d.previous]), 1)
      : 1;

  const maxPopularBookings =
    data.popularTimes.length > 0
      ? Math.max(...data.popularTimes.map((p) => p.bookings), 1)
      : 1;

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={() => navigate("/bookings?action=new")}
          disabled={!subscriptionActive}
          className="rounded-full bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 h-auto"
        >
          <CalendarIcon className="w-4 h-4 mr-2" />
          {t("dashboard.home.actions.newBooking")}
        </Button>
        {!isStaff && (
          <>
            <Button
              variant="outline"
              onClick={() => navigate("/courts")}
              className="rounded-full px-5 py-2.5 h-auto border-neutral-200"
            >
              <MapPin className="w-4 h-4 mr-2" />
              {t("dashboard.home.actions.viewCourts")}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/settings")}
              className="rounded-full px-5 py-2.5 h-auto border-neutral-200"
            >
              <Settings className="w-4 h-4 mr-2" />
              {t("dashboard.home.actions.viewSettings")}
            </Button>
          </>
        )}
        <ShareClubButton
          clubId={clubId}
          className="rounded-full px-5 py-2.5 h-auto border-neutral-200"
        />
      </div>

      {/* Stats Cards */}
      <div
        className={`grid grid-cols-1 sm:grid-cols-2 ${showRevenue ? "xl:grid-cols-4" : "xl:grid-cols-3"} gap-4`}
      >
        <StatCard
          label={t("dashboard.home.stats.today")}
          value={data.today.value}
          unit={t("dashboard.home.stats.todayUnit")}
          icon={<CalendarDays className="w-4 h-4" />}
          iconColor="text-primary-600"
          iconBg="bg-primary-50"
          trendData={data.today}
          trendLabel={t("dashboard.home.stats.vsYesterday")}
          trendFormatter={(c) => formatNumber(Math.abs(c))}
        />
        <StatCard
          label={t("dashboard.home.stats.thisWeek")}
          value={data.week.value}
          unit={t("dashboard.home.stats.weekUnit")}
          icon={<TrendingUp className="w-4 h-4" />}
          iconColor="text-blue-600"
          iconBg="bg-blue-50"
          trendData={data.week}
          trendLabel={t("dashboard.home.stats.vsLastWeek")}
          trendFormatter={(c) => formatNumber(Math.abs(c))}
        />
        <StatCard
          label={t("dashboard.home.stats.occupancy")}
          value={`${data.occupancy.value}%`}
          unit={t("dashboard.home.stats.occupancySub")}
          icon={<BarChart3 className="w-4 h-4" />}
          iconColor="text-amber-600"
          iconBg="bg-amber-50"
          trendData={data.occupancy}
          trendLabel={t("dashboard.home.stats.vsLastWeek")}
          trendFormatter={(c) => `${Math.abs(c)}%`}
        />
        {showRevenue && data.revenue && (
          <StatCard
            label={t("dashboard.home.stats.revenue")}
            value={formatPriceShort(data.revenue.value)}
            unit="RSD"
            icon={<Banknote className="w-4 h-4" />}
            iconColor="text-emerald-600"
            iconBg="bg-emerald-50"
            trendData={data.revenue}
            trendLabel={t("dashboard.home.stats.vsLastWeek")}
            trendFormatter={(c) => `${formatPriceShort(Math.abs(c))} RSD`}
          />
        )}
      </div>

      {/* Upcoming + Court Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <div className="bg-white rounded-xl p-4 sm:p-6 border border-neutral-200">
          <h3 className="font-heading font-semibold text-neutral-800 mb-4">
            {t("dashboard.home.upcoming.title")}
          </h3>
          {data.upcomingBookings.length === 0 ? (
            <p className="text-sm text-neutral-400 text-center py-8">
              {t("dashboard.home.upcoming.empty")}
            </p>
          ) : (
            <div className="space-y-2">
              {data.upcomingBookings.map((b) => (
                <Link
                  key={b.id}
                  to={`/bookings?bookingId=${b.id}`}
                  className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex items-center justify-center w-11 h-11 bg-primary-50 rounded-lg shrink-0">
                      <Clock className="w-4 h-4 text-primary-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-neutral-800 truncate">
                        {b.playerName}
                      </p>
                      <p className="text-xs text-neutral-500 truncate">
                        {b.startTime} •{" "}
                        {formatCourtLabel(
                          b.courtName,
                          t("bookings.form.court"),
                        )}{" "}
                        • {b.date}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-full font-medium shrink-0 ml-2">
                    {t(`sports.${b.sportName}`)}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl p-4 sm:p-6 border border-neutral-200">
          <h3 className="font-heading font-semibold text-neutral-800 mb-4">
            {t("dashboard.home.courtStatus.title")}
          </h3>
          {data.courtStatus.length === 0 ? (
            <p className="text-sm text-neutral-400 text-center py-8">
              {t("courts.empty")}
            </p>
          ) : (
            <div className="space-y-2">
              {data.courtStatus.map((c) => (
                <div
                  key={c.courtId}
                  className="flex items-center justify-between p-3 border border-neutral-200 rounded-lg"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                        c.status === "free" ? "bg-emerald-500" : "bg-red-500"
                      }`}
                    />
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-neutral-800 truncate">
                        {c.name}{" "}
                        <span className="text-neutral-400 font-normal">
                          · {t(`sports.${c.sportName}`)}
                        </span>
                      </p>
                      <p className="text-xs text-neutral-500 truncate">
                        {c.status === "occupied"
                          ? t("dashboard.home.courtStatus.until", {
                              time: c.until ?? "",
                            })
                          : c.nextStartTime
                            ? t("dashboard.home.courtStatus.next", {
                                time: c.nextStartTime,
                              })
                            : t("dashboard.home.courtStatus.noNext")}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`text-[10px] font-semibold px-2 py-1 rounded-full uppercase tracking-wide shrink-0 ml-2 ${
                      c.status === "free"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {c.status === "free"
                      ? t("dashboard.home.courtStatus.freeBadge")
                      : t("dashboard.home.courtStatus.occupiedBadge")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Coaches overview — managers/superadmin, coaching-enabled clubs only */}
      {!isStaff && (club?.allowCoaching ?? false) && (
        <CoachesWidget clubId={clubId} />
      )}

      {/* Weekly Revenue Chart */}
      {showRevenue && data.weeklyRevenue && (
        <WeeklyRevenueChart
          weeklyRevenue={data.weeklyRevenue}
          maxRevenueDay={maxRevenueDay}
        />
      )}

      {/* Popular Times */}
      <div className="bg-white rounded-xl p-4 sm:p-6 border border-neutral-200">
        <h3 className="font-heading font-semibold text-neutral-800 mb-4">
          {t("dashboard.home.popularTimes.title")}
        </h3>
        {data.popularTimes.length === 0 ? (
          <p className="text-sm text-neutral-400 text-center py-8">
            {t("dashboard.home.popularTimes.empty")}
          </p>
        ) : (
          <div className="space-y-2.5">
            {data.popularTimes.map((p) => (
              <div key={p.hour} className="flex items-center gap-4">
                <span className="text-sm font-medium text-neutral-500 w-14 shrink-0">
                  {p.hour}
                </span>
                <div className="flex-1 bg-neutral-100 rounded-full h-7 overflow-hidden">
                  <div
                    className="bg-primary-500 h-full rounded-full flex items-center justify-end pr-3 transition-all"
                    style={{
                      width: `${Math.max((p.bookings / maxPopularBookings) * 100, 8)}%`,
                    }}
                  >
                    <span className="text-xs font-semibold text-white">
                      {p.bookings}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Coaching overview — only rendered for managers/superadmin on coaching-enabled
// clubs. Shows each active coach's upcoming session count plus club totals.
function CoachesWidget({ clubId }: { clubId: string }) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ["admin", "coaches", "stats", clubId],
    queryFn: () =>
      apiGet<CoachStats>(`/api/admin/clubs/${clubId}/coaches/stats`),
    refetchInterval: 60_000,
  });
  if (!data) return null;
  return (
    <div className="bg-white rounded-xl p-4 sm:p-6 border border-neutral-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-neutral-800 flex items-center gap-2">
          <GraduationCap className="w-4 h-4 text-primary-500" />
          {t("dashboard.home.coaches.title")}
        </h3>
        <Link
          to="/coaches"
          className="text-sm text-primary-600 hover:underline"
        >
          {t("dashboard.home.coaches.manage")}
        </Link>
      </div>
      {data.coaches.length === 0 ? (
        <p className="text-sm text-neutral-400 text-center py-6">
          {t("dashboard.home.coaches.empty")}
        </p>
      ) : (
        <>
          <p className="text-sm text-neutral-500 mb-3">
            {t("dashboard.home.coaches.summary", {
              upcoming: data.totalUpcoming,
              next7: data.next7Sessions,
            })}
          </p>
          <div className="space-y-2">
            {data.coaches.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                    <GraduationCap className="w-4 h-4 text-primary-600" />
                  </div>
                  <p className="font-medium text-sm text-neutral-800 truncate">
                    {c.name}
                  </p>
                </div>
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded-full font-medium shrink-0 ml-2">
                  {c.upcoming} {t("dashboard.home.coaches.sessionsShort")}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function WeeklyRevenueChart({
  weeklyRevenue,
  maxRevenueDay,
}: {
  weeklyRevenue: NonNullable<DashboardData["weeklyRevenue"]>;
  maxRevenueDay: number;
}) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <div className="bg-white rounded-xl p-4 sm:p-6 border border-neutral-200">
      <div className="flex items-center justify-between mb-4 sm:mb-6 flex-wrap gap-3">
        <h3 className="font-heading font-semibold text-neutral-800">
          {t("dashboard.home.weeklyRevenue.title")}
        </h3>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-primary-500 rounded-sm" />
            <span className="text-neutral-600">
              {t("dashboard.home.weeklyRevenue.current")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-neutral-300 rounded-sm" />
            <span className="text-neutral-600">
              {t("dashboard.home.weeklyRevenue.previous")}
            </span>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto -mx-2 px-2">
        <div className="flex items-end gap-2 sm:gap-3 h-48 min-w-[420px]">
          {weeklyRevenue.map((d) => {
            const isHovered = hovered === d.dayKey;
            return (
              <div
                key={d.dayKey}
                className="flex-1 flex flex-col items-center gap-2 min-w-0"
                onMouseEnter={() => setHovered(d.dayKey)}
                onMouseLeave={() =>
                  setHovered((current) =>
                    current === d.dayKey ? null : current,
                  )
                }
              >
                <div className="relative w-full flex gap-1 items-end h-40">
                  {isHovered && (
                    <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-10 rounded-md bg-neutral-800 text-white text-xs px-2.5 py-1.5 shadow-lg whitespace-nowrap space-y-0.5">
                      <div className="font-semibold">
                        {t(`days.long.${d.dayKey}`)}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 bg-primary-400 rounded-sm" />
                        <span className="text-white/80">
                          {t("dashboard.home.weeklyRevenue.current")}:
                        </span>
                        <span className="font-semibold">
                          {formatRSD(d.current)} RSD
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 bg-neutral-400 rounded-sm" />
                        <span className="text-white/80">
                          {t("dashboard.home.weeklyRevenue.previous")}:
                        </span>
                        <span className="font-semibold">
                          {formatRSD(d.previous)} RSD
                        </span>
                      </div>
                    </div>
                  )}
                  <div
                    className={`flex-1 rounded-t transition-colors ${
                      isHovered ? "bg-neutral-400" : "bg-neutral-300"
                    }`}
                    style={{
                      height: `${Math.max((d.previous / maxRevenueDay) * 100, 2)}%`,
                    }}
                  />
                  <div
                    className={`flex-1 rounded-t transition-colors ${
                      isHovered ? "bg-primary-600" : "bg-primary-500"
                    }`}
                    style={{
                      height: `${Math.max((d.current / maxRevenueDay) * 100, 2)}%`,
                    }}
                  />
                </div>
                <span className="text-xs font-medium text-neutral-500">
                  {t(`days.short.${d.dayKey}`)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const { user } = useMe();
  const role = user?.role || "STAFF";

  const titleKey =
    role === "SUPERADMIN"
      ? "dashboard.home.welcomeSuperadmin"
      : role === "MANAGER"
        ? "dashboard.home.welcomeManager"
        : "dashboard.home.welcomeStaff";

  const dateLocale =
    i18n.language === "ru"
      ? "ru-RU"
      : i18n.language === "en"
        ? "en-US"
        : "sr-Latn-RS";
  const dateLabel = new Date().toLocaleDateString(dateLocale, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-7xl mx-auto">
      <h1 className="font-heading text-xl sm:text-2xl font-bold text-neutral-800 mb-1">
        {t(titleKey)}
      </h1>
      <p className="text-sm text-neutral-500 mb-6">{dateLabel}</p>

      <ClubScopeShell>
        {(clubId) => <DashboardContent clubId={clubId} />}
      </ClubScopeShell>
    </div>
  );
}
